import { useMemo } from 'react';

import { useCcpEntitlementQuery } from './CcpEntitlementQuery.generated';
import { getAtlOrigin } from './getAtlOrigin';

export const useCcpBillingExperience = ({
  premiumFeatures,
  entitlementId,
}: {
  premiumFeatures: string[];
  entitlementId?: string | null;
}) => {
  const atlOriginHash = useMemo(() => getAtlOrigin(), []);
  const isCcpBillingEnabled = premiumFeatures?.includes('ccpBilling');
  const { data: ccpData, loading: ccpDataLoading } = useCcpEntitlementQuery({
    waitOn: ['None'],
    variables: {
      id: entitlementId!,
    },
    skip: !isCcpBillingEnabled || !entitlementId,
  });

  if (!isCcpBillingEnabled || !entitlementId) {
    return {
      ccpDataLoading: false,
      isCcpBillingEnabled: false,
      canManageBilling: false,
      billingPageUrl: '',
      changePlanUrl: '',
    };
  }

  const entitlement = ccpData?.ccp?.entitlement;
  const changePlanExperience =
    entitlement?.experienceCapabilities?.changeOfferingV2;
  const manageEntitlementExperience =
    entitlement?.experienceCapabilities?.manageEntitlement;
  const canManageBilling =
    manageEntitlementExperience?.isAvailableToUser ?? false;

  /**
   * @see https://hello.atlassian.net/wiki/spaces/BILLIONS/pages/2653244589/Deeplinks+in+CCP+experience
   */
  let billingPageUrl = '';
  if (manageEntitlementExperience?.experienceUrl) {
    const url = new URL(manageEntitlementExperience.experienceUrl);
    url.searchParams.set('atlOrigin', atlOriginHash);
    billingPageUrl = url.toString();
  }

  let changePlanUrl = '';
  if (changePlanExperience?.experienceUrl) {
    const url = new URL(changePlanExperience.experienceUrl);
    url.searchParams.set('atlOrigin', atlOriginHash);
    changePlanUrl = url.toString();
  }

  return {
    ccpDataLoading,
    isCcpBillingEnabled,
    canManageBilling,
    billingPageUrl,
    changePlanUrl,
  };
};
